import React, { useEffect, useState, useRef } from 'react';
import { Box } from '@chakra-ui/react';
import { useAnimationPause } from '../../hooks/useAnimationPause';
import MemoryManager from '../../utils/MemoryManager';
import './BurrowSpinner.css';

const BurrowSpinner = ({ size = 'md', color = 'blue.500', ...props }) => {
  // Pause animations when spinner is off-screen
  const { ref: animationRef } = useAnimationPause({ enabled: true });
  
  // Convert Chakra UI size to pixel values
  const sizeMap = {
    xs: '1rem',
    sm: '1.5rem',
    md: '2rem',
    lg: '3rem',
    xl: '4rem'
  };
  
  const pixelSize = sizeMap[size] || size;
  
  // These paths should point to the correct location of your spinner images
  // Updated to match actual filenames in public/spinner-images/
  const spinnerPt1 = process.env.PUBLIC_URL + '/spinner-images/burrow-spinner-mind-blow_pt1.png';
  const spinnerPt2 = process.env.PUBLIC_URL + '/spinner-images/burrow-spinner-mind-blow_pt2.png';
  const spinnerPt3 = process.env.PUBLIC_URL + '/spinner-images/burrow-spinner-mind-blow_pt3.png';
  
  // Fallback spinner if images don't load
  const fallbackSpinner = 'https://bunny-burrow-pull-1.b-cdn.net/Burrow-Spinner-100.png';
  
  useEffect(() => {
    // Preload images for smoother initial display
    const preloadImages = () => {
      console.log('Preloading BurrowSpinner images from:', spinnerPt1);
      [spinnerPt1, spinnerPt2, spinnerPt3, fallbackSpinner].forEach(src => {
        const img = new Image();
        img.src = src;
        img.onload = () => console.log(`Successfully loaded: ${src}`);
        img.onerror = (e) => console.error(`Failed to load: ${src}`, e);
      });
    };
    
    preloadImages();
  }, [spinnerPt1, spinnerPt2, spinnerPt3, fallbackSpinner]);
  
  // Animation speed state (in seconds)
  const [pt2Duration, setPt2Duration] = useState(3); // initial duration for pt2
  const [pt3Duration, setPt3Duration] = useState(2); // initial duration for pt3

  // Refs to keep track of direction and current duration
  const pt2Ref = useRef({ duration: 3, direction: 1 }); // -1: speed up, 1: slow down
  const pt3Ref = useRef({ duration: 2, direction: 1 }); // 1: speed up, -1: slow down

  // Animate pt2 (middle layer)
  useEffect(() => {
    const minDuration = 3.5;
    const maxDuration = 6.0;
    // Randomize initial duration, always right (spin-right / clockwise)
    pt2Ref.current.duration = Math.random() * (maxDuration - minDuration) + minDuration;
    setPt2Duration(Number(pt2Ref.current.duration.toFixed(2)));
    const step = 0.020; // slightly faster speed change
    const interval = MemoryManager.setInterval(() => {
      // Only change speed, never direction
      let nextDuration = pt2Ref.current.duration + pt2Ref.current.direction * step;
      if (nextDuration <= minDuration) {
        nextDuration = minDuration;
      } else if (nextDuration >= maxDuration) {
        nextDuration = maxDuration;
      }
      pt2Ref.current.duration = nextDuration;
      setPt2Duration(Number(pt2Ref.current.duration.toFixed(2)));
    }, 30, 'BurrowSpinner-pt2-animation');
    return () => MemoryManager.clearInterval(interval);
  }, []);

  // Animate pt3 (top layer)
  useEffect(() => {
    const minDuration = 2.5;
    const maxDuration = 4.5;
    // Randomize initial duration, always left (spin-left / counter-clockwise)
    pt3Ref.current.duration = Math.random() * (maxDuration - minDuration) + minDuration;
    setPt3Duration(Number(pt3Ref.current.duration.toFixed(2)));
    const step = 0.011; // slightly faster speed change
    const interval = MemoryManager.setInterval(() => {
      // Only change speed, never direction
      let nextDuration = pt3Ref.current.duration + pt3Ref.current.direction * step;
      if (nextDuration <= minDuration) {
        nextDuration = minDuration;
      } else if (nextDuration >= maxDuration) {
        nextDuration = maxDuration;
      }
      pt3Ref.current.duration = nextDuration;
      setPt3Duration(Number(pt3Ref.current.duration.toFixed(2)));
    }, 30, 'BurrowSpinner-pt3-animation');
    return () => MemoryManager.clearInterval(interval);
  }, []);

  return (
    <Box 
      ref={animationRef}
      className="burrow-spinner-container"
      position="relative"
      display="inline-block"
      width={pixelSize}
      height={pixelSize}
      {...props}
      aria-label="Loading"
      role="status"
    >
      {/* Stationary base layer */}
      <img 
        src={spinnerPt1} 
        className="burrow-spinner-layer stationary"
        alt=""
        onLoad={() => console.log('Burrow spinner base layer loaded')}
        onError={(e) => {
          console.error('Failed to load spinner base layer', e);
          e.target.src = fallbackSpinner;
        }}
      />
      
      {/* Clockwise spinning middle layer */}
      <img 
        src={spinnerPt2} 
        className="burrow-spinner-layer spin-right"
        alt=""
        style={{ animationDuration: pt2Duration + 's' }}
        onError={(e) => {
          console.error('Failed to load spinner middle layer', e);
          e.target.style.display = 'none';
        }}
      />
      
      {/* Counter-clockwise spinning top layer */}
      <img 
        src={spinnerPt3} 
        className="burrow-spinner-layer spin-left"
        alt=""
        style={{ animationDuration: pt3Duration + 's' }}
        onError={(e) => {
          console.error('Failed to load spinner top layer', e);
          e.target.style.display = 'none';
        }}
      />
    </Box>
  );
};

export default BurrowSpinner;
